import { apiFetch } from '../../config/api';

/**
 * Theme Service
 * Handles theme-related API operations
 */
export const themeService = {
  /**
   * Load tenant active theme
   */
  async loadTenantTheme() {
    try {
      const response = await apiFetch(`/api/v1/tenant/themes/active`);
      return response || null;
    } catch (error) {
      // Don't throw - allow app to continue with default theme
      return null;
    }
  },

  /**
   * Load all themes for a tenant with pagination
   */
  async loadTenantThemes(page = 1, pageSize = 10) {
    const url = `/api/v1/tenant/themes?page=${page}&page_size=${pageSize}`;
    const response = await apiFetch(url);
    // Handle different response structures
    const themesArray = response.themes || response.data?.themes || [];
    // Transform API response to match component expectations
    return themesArray.map(theme => ({
      ...theme,
      is_active: theme.status === 'active' || theme.is_active,
      theme_code: theme.theme_id || theme.theme_code,
      description: theme.theme_description || theme.description,
    }));
  },

  /**
   * Update tenant theme
   */
  async updateTenantTheme(theme) {
    const response = await apiFetch(`/api/v1/tenant/themes`, {
      method: 'PUT',
      body: JSON.stringify(theme),
    });
    return response.data;
  },

  /**
   * Create new theme
   */
  async createTheme(themeData) {
    // Map theme data to API format (ThemeCreateRequest)
    const apiThemeData = {
      theme_name: themeData.theme_name || 'Untitled Theme',
      theme_description:
        themeData.theme_description || themeData.description || '',
      primary_color: themeData.primary_color || '#0A84FF',
      secondary_color: themeData.secondary_color || '#2563EB',
      accent_color: themeData.accent_color || '#3B82F6',
      background_color: themeData.background_color || '#F5F7FA',
      text_color: themeData.text_color || '#1A1A1A',
      font_family: themeData.font_family || 'Inter',
      font_size: themeData.font_size || '14px',
      logo_url: themeData.logo_url || '',
      favicon_url: themeData.favicon_url || '',
      custom_css: themeData.custom_css || '',
      metadata: {
        surface_color: themeData.surface_color,
        sidebar_background: themeData.sidebar_background,
        sidebar_text: themeData.sidebar_text,
        text_secondary: themeData.text_secondary,
        text_muted: themeData.text_muted,
        divider_color: themeData.divider_color,
        success_color: themeData.success_color,
        warning_color: themeData.warning_color,
        error_color: themeData.error_color,
        info_color: themeData.info_color,
        border_radius: themeData.border_radius,
        spacing_unit: themeData.spacing_unit,
        mode: themeData.mode,
        vibe: themeData.vibe,
      },
    };

    const response = await apiFetch(`/api/v1/tenant/themes`, {
      method: 'POST',
      body: JSON.stringify(apiThemeData),
    });

    return response.data || response;
  },

  /**
   * Update theme by ID
   */
  async updateTheme(themeId, themeData) {
    // Map theme data to API format (ThemeUpdateRequest) - all fields optional
    const apiThemeData = {};

    // Only include fields that are present in themeData
    if (themeData.theme_name !== undefined)
      apiThemeData.theme_name = themeData.theme_name;
    if (themeData.theme_description !== undefined)
      apiThemeData.theme_description =
        themeData.theme_description || themeData.description;
    if (themeData.primary_color !== undefined)
      apiThemeData.primary_color = themeData.primary_color;
    if (themeData.secondary_color !== undefined)
      apiThemeData.secondary_color = themeData.secondary_color;
    if (themeData.accent_color !== undefined)
      apiThemeData.accent_color = themeData.accent_color;
    if (themeData.background_color !== undefined)
      apiThemeData.background_color = themeData.background_color;
    if (themeData.surface_color !== undefined)
      apiThemeData.surface_color = themeData.surface_color;
    if (themeData.text_color !== undefined)
      apiThemeData.text_color = themeData.text_color;
    if (themeData.font_family !== undefined)
      apiThemeData.font_family = themeData.font_family;
    if (themeData.font_size !== undefined)
      apiThemeData.font_size = themeData.font_size;
    if (themeData.heading_font_size !== undefined)
      apiThemeData.heading_font_size = themeData.heading_font_size;
    if (themeData.font_weight_normal !== undefined)
      apiThemeData.font_weight_normal = themeData.font_weight_normal;
    if (themeData.font_weight_bold !== undefined)
      apiThemeData.font_weight_bold = themeData.font_weight_bold;
    if (themeData.border_radius !== undefined)
      apiThemeData.border_radius = themeData.border_radius;
    if (themeData.spacing_unit !== undefined)
      apiThemeData.spacing_unit = themeData.spacing_unit;
    if (themeData.logo_url !== undefined)
      apiThemeData.logo_url = themeData.logo_url;
    if (themeData.favicon_url !== undefined)
      apiThemeData.favicon_url = themeData.favicon_url;
    if (themeData.custom_css !== undefined)
      apiThemeData.custom_css = themeData.custom_css;
    if (themeData.is_active !== undefined)
      apiThemeData.is_active = themeData.is_active;

    // Store additional fields in metadata
    const metadata = {};
    if (themeData.sidebar_background !== undefined)
      metadata.sidebar_background = themeData.sidebar_background;
    if (themeData.sidebar_text !== undefined)
      metadata.sidebar_text = themeData.sidebar_text;
    if (themeData.text_secondary !== undefined)
      metadata.text_secondary = themeData.text_secondary;
    if (themeData.text_muted !== undefined)
      metadata.text_muted = themeData.text_muted;
    if (themeData.divider_color !== undefined)
      metadata.divider_color = themeData.divider_color;
    if (themeData.success_color !== undefined)
      metadata.success_color = themeData.success_color;
    if (themeData.warning_color !== undefined)
      metadata.warning_color = themeData.warning_color;
    if (themeData.error_color !== undefined)
      metadata.error_color = themeData.error_color;
    if (themeData.info_color !== undefined)
      metadata.info_color = themeData.info_color;
    if (themeData.mode !== undefined) metadata.mode = themeData.mode;
    if (themeData.vibe !== undefined) metadata.vibe = themeData.vibe;

    if (Object.keys(metadata).length > 0) {
      apiThemeData.metadata = metadata;
    }

    const response = await apiFetch(`/api/v1/tenant/themes/${themeId}`, {
      method: 'PUT',
      body: JSON.stringify(apiThemeData),
    });

    return response;
  },

  /**
   * Delete theme by ID
   */
  async deleteTheme(themeId) {
    await apiFetch(`/api/v1/tenant/themes/${themeId}`, {
      method: 'DELETE',
    });
    return true;
  },

  /**
   * Activate theme by ID
   */
  async activateTheme(themeId) {
    const response = await apiFetch(
      `/api/v1/tenant/themes/${themeId}/activate`,
      {
        method: 'PATCH',
      }
    );
    return response;
  },
};
